/* tslint:disable */
/* eslint-disable */
/**
 * REAP API
 * Public REST API for REAP (multi-tenant, agentic AI for network operations).  ### Identifier conventions - **id**: UUID — canonical and used for foreign keys and writes. - **slug**: immutable, human-readable handle (kebab-case) used in configs/URLs. Unique per scope (org) or globally (orgs). - **name**: human-friendly display label (mutable). Do **not** use as a reference key.  ## Authentication Overview  This API supports local credentials (password, passkeys), SSO (OIDC/SAML), and email-verified bootstraps for registration/reset/invitations. The design is enumeration-resistant and minimizes clicks for the common case (daily sign-in).  ### Daily sign-ins (no emailTx required) - **Password**: `POST /auth/password/verify` with `{ email, password }` → returns **loginTx**. - **Passkey (email-first)**: `POST /auth/passkeys/assert` with `{ email }` → WebAuthn options + `challengeToken`   → `POST /auth/passkeys/assert/verify` → **loginTx**. - **Usernameless passkey** (optional): `POST /auth/passkeys/assert` with `{ usernameless: true }`.  Exchange **loginTx** at `POST /auth/token`: - If already org-bound (SSO, single org, or server inference) → returns **TokenResponse**. - If not org-bound and multiple orgs exist → returns **200** with `{ nextStep: \"pickOrg\", memberships:[...] }`.   The server **SHOULD filter out ineligible orgs** so the client can re-call `/auth/token` with `orgId`   without additional policy checks. - To **switch orgs** later, call `POST /auth/token` with `grantType:\"refresh\"` and `orgId`.  ### SSO (OIDC/SAML) - IdP flows produce **`oidcTx`** or **`samlTx`**, which you exchange at `POST /auth/token`. - HRD (home realm discovery): `POST /auth/discover/start` always replies **200** with a discriminated   `nextStep`:     - `sso_redirect` (single, safe target) — redirect using `connection.startUrl` (optionally include `login_hint`).     - `picker` (multiple targets) — render an org/connection picker.     - `verify_email` — prove mailbox control before revealing org/connection detail.   *No membership/policy status leaks at Start; the rich, email-proven view is returned by `/auth/discover/verify`.*  Email verification is a common bootstrap across Discovery and Invitations. Invitation Accept returns emailTx plus invited-org context (policy and SSO connections) without enumerating other orgs. Clients should drive UX from these fields and exchange loginTx/oidcTx/samlTx at `/auth/token`.  ### Email-verified bootstraps (keep `emailTx`) - **Register password/passkey**, **reset password**, and **invitation acceptance** require proving email ownership.   After `/auth/discover/verify` (or `/auth/invitations/accept`) you get **`emailTx`** for these operations or to   obtain a limited pre-org session at `/auth/token` (for example, to create the first org).  ### Enumeration resistance & security - Return generic errors for failed sign-in; normalize response timing and apply rate-limits with small jitter. - Avoid revealing user/org status or policy in pre-verification responses. - Use HTTPS; set appropriate cache controls; consider device-bound refresh tokens and short-lived access tokens.  ### Client authentication REAP supports two client auth mechanisms:  - **Bearer JWT** (`Authorization: Bearer <jwt>`): short-lived, user/session tokens returned by `/auth/token`.   Backends validate these locally via signature/claims.  - **API Key** (`X-Api-Key: <secret>`): long-lived, org-scoped secrets intended for automation (CI/CD, headless jobs).   Keys are validated by REAP’s auth layer; they are not JWTs and should not be sent as Bearer tokens.  **Guidance** - Prefer **Bearer JWTs** for interactive UIs and short-lived clients. - Use **API keys** for machine-to-machine integrations; rotate or **PATCH** `expiresAt` to extend, and **DELETE** (revoke) when done. - Assign permissions via **roles**: provide `roleIds` on create/patch; responses include `rolesResolved` for admin UX. - Do not place API keys in URLs or logs. Keys are shown **once** on creation.  ### API key return semantics - The full API key **secret is returned only in the 201 Create response** of `/orgs/{orgId}/api-keys`. - It is **never** returned by GET/PATCH/DELETE. - Clients should display it once and prompt the operator to store it securely (password manager, vault). - For subsequent requests, present the key as `X-Api-Key: <secret>`; do **not** use the Bearer scheme for API keys.  ## Auth policies (overview) - **SSO JIT (just-in-time) provisioning** can be enabled per org: SSO users can sign in without invitations. - **Invite-only SSO** (optional): require an invitation before SSO users can join the org. - **Local accounts** (optional): allow bootstrap/break-glass users without SSO. - **Discover** avoids user enumeration; org memberships and sign-in options are revealed only after email verification. - **Invitations** never expose tokens/links via API; the backend emails the invite link to the recipient. - **Org auth policy** is a **singleton** at `/orgs/{orgId}/auth/policy` that controls JIT, invite-only SSO, and local-account allowances.   The policy is retrieved/updated via **GET/PUT** and versioned with **ETag**.  ## Discovery result (structure) - **Local methods are global**: `DiscoverVerifyResponse.localConnections` lists user-level methods (`passkey`, `password`) available for the verified email — shown **once** in the UI. - **SSO is per-org**: `memberships[].connections` lists **org-scoped** SSO options (`oidc`, `saml`). This array may be **empty** if the org has no SSO. - **Policy hint**: `memberships[].policy.requiresSso` indicates that the org **requires** SSO (local login should not grant access there), even if local methods exist globally.  ## Passwordless authentication (overview) - New-user and invitation flows converge on **email verification** → short-lived **`emailTx`** (no user enumeration). - Clients then use **Passkeys** (WebAuthn/FIDO2) to enroll a credential (attestation) or sign in (assertion). - Successful passkey assertion yields **`loginTx`**, which is exchanged at **`POST /auth/token`** with `grantType=login_tx`. - **Token grants** supported at `/auth/token`:     - **authorization_code** (OIDC) via **`oidcTx`**     - **saml2_bearer** (SAML) via **`ssoTx`**     - **login_tx** (Passkey) via **`loginTx`**     - **refresh_token** (rotation; optional `orgId` switch)     - **email_tx** (bootstrap pre-org; limited to safe endpoints like creating the first org) - **Stateless challenge** option for passkeys: options responses include a signed **`challengeToken`** that the client must echo on verify,   allowing the server to validate the challenge/guardrails **without storing per-challenge state** (short TTL recommended). - **Optional fast-path**: `POST /auth/passkeys/attest/verify?issueLoginTx=true` may return `loginTx` immediately after registration   (policy permitting), otherwise the default flow is attest → assert → token. - **Usernameless (passkey-first) sign-in**: the portal may skip email/Discover and call   `POST /auth/passkeys/assert` with `{ \"usernameless\": true }`. The server returns   `PublicKeyCredentialRequestOptions` with an empty/omitted `allowCredentials`. The browser’s   Conditional UI prompts if a matching passkey exists; on success, the client verifies at   `/auth/passkeys/assert/verify` to obtain `loginTx`, then exchanges at `/auth/token`.  ## API response conventions - **No envelopes**: endpoints return typed bodies directly. - **Resources**: single GET/POST returns the resource object (composed from `ResourceBase` and `OrgScoped` when applicable). - **Operational endpoints** (e.g., auth/token, passkeys) return typed operation results (no `ResourceBase`). - **Lists**: return `{ items: [...], meta: { ...} }`, where each item is a full resource object and `meta` follows **PageMeta**. Responses MAY include `Link` headers (`rel=\"next\"`, `rel=\"prev\"`). When `meta.exactTotal` is omitted clients SHOULD treat totals as unknown and render “more available” messaging rather than fabricating counts.  ### Cache-Control conventions - **Mutable resource detail** (single GET, POST, PATCH) and every response that carries credentials, tokens, or bootstrap data sends `Cache-Control: no-store`. Browsers MUST revalidate before rendering drawers/forms so operators see their latest edits. - **Collection/list endpoints** that back dashboards or tables may use `Cache-Control: private, max-age=15` together with `ETag` and `Link` headers. Clients SHOULD revalidate on manual refresh (If-None-Match) and treat the TTL as an upper bound. - **Streaming/event feeds** and download endpoints for bootstrap bundles always respond with `Cache-Control: no-store`. - If a response omits `Cache-Control`, treat it as non-cacheable.  ### Identity & scoping - Every object is keyed by `id` (UUID). - Common audit fields come from `ResourceBase` (`createdAt`, `updatedAt`, `createdBy`, `updatedBy`). - Multi-tenant scoping builds on `OrgScoped` (adds `orgId`); request bodies never include `orgId`—it comes from the path.  ### Pagination & sorting - Pagination via `limit` (1–200, default 50), `after`, `before`. Servers SHOULD return `Link` headers that mirror `meta.next`/`meta.prev` and include `meta.exactTotal` when a precise count is available. - `meta.exactTotal` is OPTIONAL. When omitted it signals “total unknown”; when the repository already computes a count (e.g., `COUNT(*)`) servers SHOULD populate it. - Sort (when supported) is a comma-separated list; prefix with `-` for descending (for example `-lastSeenAt,mgmtAddress`).  ### Concurrency & caching - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match** (mismatch → **412**). - `GET` supports **If-None-Match** and may return **304 Not Modified**. - Each response includes **`X-Request-Id`** for troubleshooting.  ### Conditional GETs (ETag / If-None-Match) - Servers return **ETag** headers on GETs for cache-friendly revalidation. - Clients can send **If-None-Match** with the last seen ETag:   - If representation is unchanged → **304 Not Modified** (no body).   - If changed → **200 OK** with a new ETag and the latest representation. - Recommended for frequently-polled resources (e.g., devices list, discovery runs, topology graphs).  ### Error model - Structured errors use the `Problem` schema. - Common statuses: **400** (validation), **401/403** (auth/authz), **404** (not found), **409** (conflict), **412** (ETag mismatch), **429** (rate limit).  ### Credentials model (overview) - **Secret vs Credential vs Assignment**   - **Secret**: write-only value + metadata (`type`, `fingerprint`, `last4`). Value is never returned.   - **Credential**: one access **kind** (`cli_ssh`, `snmp_v2c`, `snmp_v3`, `gnmi`, `api_rest`) with kind-specific fields that **reference Secret IDs** (e.g., SSH key, SNMP passwords, API key, mTLS certs).   - **Credential Assignment**: routing rule that applies a **single credential** by scope (org / sites / specific devices) with optional match filters (hostname regex/prefix/suffix, labels, vendor/model).   - Rotate a secret’s value via `POST /value` (use **If-Match** to guard against concurrent rotation). - **Validation**   - On create/update, the server validates that each referenced Secret ID is of the **expected SecretType** for the credential kind (mismatch → 400). - **Resolution order (when multiple assignments match a device)**   1. Lower `priority` first (e.g., 10 wins over 100)   2. Narrower `scope` (device > sites > org)   3. Most recent `updatedAt` - **Consistency**   - Single-resource endpoints return the resource (no envelopes).   - List endpoints return `{ items, meta }` where `meta` follows `PageMeta` and cursors are passed via `limit/after/before`. - **Concurrency & cache**   - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match**.   - `GET` supports **If-None-Match** to return **304 Not Modified** when ETag matches.  ## Devices (key behaviors)  - **States** — `discovered`, `validated`, `unreachable`, `retired`, `merged` (see `DeviceState`). - **Discovery-managed fields** (for example `vendor`, `model`, `osVersion`, `serial`, `hostname`, `firstSeenAt`, `lastSeenAt`) are **read-only** via API. - **User-editable** fields: `displayName`, `labels` (case-insensitive keys; PATCH overwrites the map), `role` (free text, e.g. `access`, `core`, `firewall`, `wireless-ap`), `notes`. - **Create** is allowed (beta/testing). Default list sort: `-lastSeenAt`. - **Retire** via `PATCH` (`state=retired`). **DELETE** is hard-delete (beta convenience). - **Merge** consolidates duplicates; merged-away device IDs subsequently return **404**.  ## Edge connectivity (Connectors & Connector Clusters)  **Connectors** are lightweight agents (AMI/OVA/etc.) that establish outbound, persistent control channels from customer sites to REAP. Connectors register to an **Organization**, can optionally belong to a **Connector Cluster**, and periodically report health, version, and capabilities.  **Connector Clusters** group one or more connectors for redundancy or capacity. Clusters can be attached to **Sites** (N↔M). The API allows make-before-break migrations: a Site can temporarily be served by multiple clusters. Backend scheduling ensures only one active worker set manages a Site at a time. - The attach result includes **multiAttached** to surface Sites now attached to this cluster and at least one other cluster.  **User-data (first-boot)**: each Connector exposes a one-time, short-lived bootstrap bundle for cloud-init. Content negotiation: - `Accept: text/cloud-config` → cloud-init YAML (default) - `Accept: application/json` → JSON wrapper (opaque to clients) - `Accept: application/base64+json` → base64 of the opaque JSON  One-time semantics: redeemed → **409 Conflict**, expired → **410 Gone**. Responses SHOULD include `Cache-Control: no-store`.  **Concurrency & caching**: mutable single-resource endpoints return **ETag** and require **If-Match** on updates/deletes. Reads MAY accept **If-None-Match** and return **304**. Lists use pagination via `{ items, meta }` (see `PageMeta`).  # SSO / IdP Connections REAP supports per-organization **IdP connections** for **OIDC** and **SAML**. - Connections are modeled as a discriminated union (`type: \"oidc\" | \"saml\"`). - Connection **config** holds IdP metadata. Some fields are **readOnly** and computed by REAP (e.g., `redirectUris` for OIDC; `acsUrl`, `spEntityId` for SAML). - **Concurrency:** single-resource GET returns **ETag**; **PATCH**_/_**DELETE** require **If-Match**. - **Deletes** return **204 No Content**. - **SAML IdP-initiated** sign-in can be allowed/blocked by `IdpSamlConfig.allowIdpInitiated` (default **true**). # OIDC client authentication - Confidential clients only: REAP supports client_secret_basic (default) and client_secret_post. - private_key_jwt and public clients are not supported in this version. - PKCE is supported but off by default; enable per connection via `config.enforcePkce`.  ## Local password authentication (optional) - Fits the same **email-first, enumeration-resistant** pattern: verify email → short-lived **`emailTx`**. - Supports:   - **Register** a password after email verification   - **Verify** password to obtain **`loginTx`** (exchange at `/auth/token` with `grantType=loginTx`)   - **Reset** password (email-verified), **Change** password (bearer session) - Client sends the **clear-text password over HTTPS**; the server performs KDF hashing (e.g., **Argon2id** with per-user salt; optional application-level pepper in KMS). - Store the **password hash (and parameters)** in the **regular user table** (not a secrets manager). If you use a **pepper**, store that pepper as a secret. - Error hardening:   - Same **lockout** semantics as passkeys (use **423** with a neutral error body)   - Consistent, non-enumerating responses for all email-bound flows
 *
 * The version of the OpenAPI document: 1.3.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as runtime from '../runtime';
import { DeviceFromJSON, DeviceAuditRecordListResponseFromJSON, DeviceCreateToJSON, DeviceMergeCandidatesFromJSON, DeviceMergeRequestToJSON, DeviceRouteTablePageFromJSON, DeviceRunningConfigDiffFromJSON, DeviceRunningConfigSnapshotFromJSON, DeviceRunningConfigVersionsListResponseFromJSON, DeviceUpdateToJSON, DevicesListResponseFromJSON, IntegrationConnectionCMDBBindingsResponseFromJSON, IntegrationConnectionCMDBSyncRequestToJSON, IntegrationConnectionCMDBSyncResponseFromJSON, IntegrationConnectionCMDBWritebackPolicyFromJSON, IntegrationConnectionCMDBWritebackRequestToJSON, IntegrationConnectionCMDBWritebackResponseFromJSON, InventoryCMDBConnectionsResponseFromJSON, ListDeviceFacets200ResponseFromJSON, } from '../models/index';
/**
 *
 */
export class DevicesApi extends runtime.BaseAPI {
    /**
     * Deprecated compatibility projection. New clients must read and manage operating policy through the Integrations CMDB API. Inventory does not authorize or execute publication.
     * Get legacy CMDB writeback policy projection
     * @deprecated
     */
    getInventoryCMDBWritebackPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getInventoryCMDBWritebackPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getInventoryCMDBWritebackPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/writeback-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBWritebackPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Deprecated compatibility projection. New clients must read and manage operating policy through the Integrations CMDB API. Inventory does not authorize or execute publication.
     * Get legacy CMDB writeback policy projection
     * @deprecated
     */
    getInventoryCMDBWritebackPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getInventoryCMDBWritebackPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List distinct device vendor and role filters
     */
    listDeviceFacetsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listDeviceFacets().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/facets`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => ListDeviceFacets200ResponseFromJSON(jsonValue));
        });
    }
    /**
     * List distinct device vendor and role filters
     */
    listDeviceFacets(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listDeviceFacetsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the current REAP device-to-CMDB CI bindings used by the inventory CMDB workflow. Results are org-scoped and can be narrowed to a single site when `siteId` is provided.
     * List CMDB bindings for inventory devices
     */
    listInventoryCMDBBindingsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listInventoryCMDBBindings().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listInventoryCMDBBindings().');
            }
            const queryParameters = {};
            if (requestParameters['siteId'] != null) {
                queryParameters['siteId'] = requestParameters['siteId'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/bindings`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBBindingsResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns the current REAP device-to-CMDB CI bindings used by the inventory CMDB workflow. Results are org-scoped and can be narrowed to a single site when `siteId` is provided.
     * List CMDB bindings for inventory devices
     */
    listInventoryCMDBBindings(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listInventoryCMDBBindingsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns CMDB-capable connection references needed to display inventory projections and confirmed bindings. Synchronization, review decisions, policy changes and publication are integrations workflows and are intentionally unavailable from the inventory page.
     * List CMDB-capable connections for read-only inventory views
     */
    listInventoryCMDBConnectionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listInventoryCMDBConnections().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InventoryCMDBConnectionsResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns CMDB-capable connection references needed to display inventory projections and confirmed bindings. Synchronization, review decisions, policy changes and publication are integrations workflows and are intentionally unavailable from the inventory page.
     * List CMDB-capable connections for read-only inventory views
     */
    listInventoryCMDBConnections(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listInventoryCMDBConnectionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns configuration audit records collected for the device when a `running-config` change was detected. Records are deduplicated by the collector before indexing.
     * List device audit records
     */
    orgsOrgIdDevicesDeviceIdAuditRecordsGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdAuditRecordsGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdAuditRecordsGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['offset'] != null) {
                queryParameters['offset'] = requestParameters['offset'];
            }
            if (requestParameters['q'] != null) {
                queryParameters['q'] = requestParameters['q'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}/audit-records`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceAuditRecordListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns configuration audit records collected for the device when a `running-config` change was detected. Records are deduplicated by the collector before indexing.
     * List device audit records
     */
    orgsOrgIdDevicesDeviceIdAuditRecordsGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdAuditRecordsGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Permanently deletes the device (hard-delete). Requires If-Match.
     * Delete device
     */
    orgsOrgIdDevicesDeviceIdDeleteRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdDelete().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdDelete().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (requestParameters['ifMatch'] != null) {
                headerParameters['If-Match'] = String(requestParameters['ifMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'DELETE',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.VoidApiResponse(response);
        });
    }
    /**
     * Permanently deletes the device (hard-delete). Requires If-Match.
     * Delete device
     */
    orgsOrgIdDevicesDeviceIdDelete(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.orgsOrgIdDevicesDeviceIdDeleteRaw(requestParameters, initOverrides);
        });
    }
    /**
     * Returns the device. Supports If-None-Match.
     * Get device
     */
    orgsOrgIdDevicesDeviceIdGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (requestParameters['ifNoneMatch'] != null) {
                headerParameters['If-None-Match'] = String(requestParameters['ifNoneMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceFromJSON(jsonValue));
        });
    }
    /**
     * Returns the device. Supports If-None-Match.
     * Get device
     */
    orgsOrgIdDevicesDeviceIdGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Partial update for user-editable fields (`displayName`, `role`, `labels`, `notes`) and setting `state` to `retired`. Requires If-Match.
     * Update device (user fields / retire)
     */
    orgsOrgIdDevicesDeviceIdPatchRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdPatch().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdPatch().');
            }
            if (requestParameters['deviceUpdate'] == null) {
                throw new runtime.RequiredError('deviceUpdate', 'Required parameter "deviceUpdate" was null or undefined when calling orgsOrgIdDevicesDeviceIdPatch().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (requestParameters['ifMatch'] != null) {
                headerParameters['If-Match'] = String(requestParameters['ifMatch']);
            }
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'PATCH',
                headers: headerParameters,
                query: queryParameters,
                body: DeviceUpdateToJSON(requestParameters['deviceUpdate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceFromJSON(jsonValue));
        });
    }
    /**
     * Partial update for user-editable fields (`displayName`, `role`, `labels`, `notes`) and setting `state` to `retired`. Requires If-Match.
     * Update device (user fields / retire)
     */
    orgsOrgIdDevicesDeviceIdPatch(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdPatchRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the projected route-table rows for a device and VRF. The response is sourced from the latest normalized route-table snapshot for the device, or from the latest snapshot whose `collectedAt <= asOf` when `asOf` is provided.
     * Get device route table
     */
    orgsOrgIdDevicesDeviceIdRouteTableGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRouteTableGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRouteTableGet().');
            }
            if (requestParameters['vrf'] == null) {
                throw new runtime.RequiredError('vrf', 'Required parameter "vrf" was null or undefined when calling orgsOrgIdDevicesDeviceIdRouteTableGet().');
            }
            const queryParameters = {};
            if (requestParameters['vrf'] != null) {
                queryParameters['vrf'] = requestParameters['vrf'];
            }
            if (requestParameters['asOf'] != null) {
                queryParameters['asOf'] = requestParameters['asOf'].toISOString();
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}/route-table`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceRouteTablePageFromJSON(jsonValue));
        });
    }
    /**
     * Returns the projected route-table rows for a device and VRF. The response is sourced from the latest normalized route-table snapshot for the device, or from the latest snapshot whose `collectedAt <= asOf` when `asOf` is provided.
     * Get device route table
     */
    orgsOrgIdDevicesDeviceIdRouteTableGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdRouteTableGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a unified diff between two running configuration snapshots. Diffs include `outputRedacted`, `redactionVersion`, and `rawPersisted` when both source snapshots have stamped protection metadata. These fields may be `null` when either source snapshot is legacy and unstamped.
     * Diff running config versions
     */
    orgsOrgIdDevicesDeviceIdRunningConfigDiffGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigDiffGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigDiffGet().');
            }
            if (requestParameters['from'] == null) {
                throw new runtime.RequiredError('from', 'Required parameter "from" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigDiffGet().');
            }
            if (requestParameters['to'] == null) {
                throw new runtime.RequiredError('to', 'Required parameter "to" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigDiffGet().');
            }
            const queryParameters = {};
            if (requestParameters['from'] != null) {
                queryParameters['from'] = requestParameters['from'];
            }
            if (requestParameters['to'] != null) {
                queryParameters['to'] = requestParameters['to'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}/running-config/diff`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceRunningConfigDiffFromJSON(jsonValue));
        });
    }
    /**
     * Returns a unified diff between two running configuration snapshots. Diffs include `outputRedacted`, `redactionVersion`, and `rawPersisted` when both source snapshots have stamped protection metadata. These fields may be `null` when either source snapshot is legacy and unstamped.
     * Diff running config versions
     */
    orgsOrgIdDevicesDeviceIdRunningConfigDiffGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdRunningConfigDiffGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the latest running configuration snapshot collected for the device. Snapshots are produced by the device-collector when the `running-config` collection job runs. Persisted snapshots include `outputRedacted`, `redactionVersion`, and `rawPersisted` when protection metadata was stamped at collection time. Legacy unstamped snapshots may return these fields as `null`.
     * Get running config (latest)
     */
    orgsOrgIdDevicesDeviceIdRunningConfigGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}/running-config`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceRunningConfigSnapshotFromJSON(jsonValue));
        });
    }
    /**
     * Returns the latest running configuration snapshot collected for the device. Snapshots are produced by the device-collector when the `running-config` collection job runs. Persisted snapshots include `outputRedacted`, `redactionVersion`, and `rawPersisted` when protection metadata was stamped at collection time. Legacy unstamped snapshots may return these fields as `null`.
     * Get running config (latest)
     */
    orgsOrgIdDevicesDeviceIdRunningConfigGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdRunningConfigGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a unified diff between the latest startup configuration and the latest running configuration. Diffs include `outputRedacted`, `redactionVersion`, and `rawPersisted` when both source snapshots have stamped protection metadata. These fields may be `null` when either source snapshot is legacy and unstamped.
     * Diff running config against startup config
     */
    orgsOrgIdDevicesDeviceIdRunningConfigStartupDiffGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigStartupDiffGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigStartupDiffGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}/running-config/startup-diff`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceRunningConfigDiffFromJSON(jsonValue));
        });
    }
    /**
     * Returns a unified diff between the latest startup configuration and the latest running configuration. Diffs include `outputRedacted`, `redactionVersion`, and `rawPersisted` when both source snapshots have stamped protection metadata. These fields may be `null` when either source snapshot is legacy and unstamped.
     * Diff running config against startup config
     */
    orgsOrgIdDevicesDeviceIdRunningConfigStartupDiffGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdRunningConfigStartupDiffGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns up to the last 5 running configuration snapshots for the device. Persisted snapshots include `outputRedacted`, `redactionVersion`, and `rawPersisted` when protection metadata was stamped at collection time. Legacy unstamped snapshots may return these fields as `null`.
     * List running config versions
     */
    orgsOrgIdDevicesDeviceIdRunningConfigVersionsGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigVersionsGet().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling orgsOrgIdDevicesDeviceIdRunningConfigVersionsGet().');
            }
            const queryParameters = {};
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/{deviceId}/running-config/versions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"deviceId"}}`, encodeURIComponent(String(requestParameters['deviceId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceRunningConfigVersionsListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns up to the last 5 running configuration snapshots for the device. Persisted snapshots include `outputRedacted`, `redactionVersion`, and `rawPersisted` when protection metadata was stamped at collection time. Legacy unstamped snapshots may return these fields as `null`.
     * List running config versions
     */
    orgsOrgIdDevicesDeviceIdRunningConfigVersionsGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesDeviceIdRunningConfigVersionsGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Paginated list of devices in the organization. Default sort is `-lastSeenAt`. Filterable by site, state, device type, vendor, model, management address, and labels.
     * List devices
     */
    orgsOrgIdDevicesGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesGet().');
            }
            const queryParameters = {};
            if (requestParameters['role'] != null) {
                queryParameters['role'] = requestParameters['role'];
            }
            if (requestParameters['limit'] != null) {
                queryParameters['limit'] = requestParameters['limit'];
            }
            if (requestParameters['after'] != null) {
                queryParameters['after'] = requestParameters['after'];
            }
            if (requestParameters['before'] != null) {
                queryParameters['before'] = requestParameters['before'];
            }
            if (requestParameters['siteId'] != null) {
                queryParameters['siteId'] = requestParameters['siteId'];
            }
            if (requestParameters['state'] != null) {
                queryParameters['state'] = requestParameters['state'];
            }
            if (requestParameters['vendor'] != null) {
                queryParameters['vendor'] = requestParameters['vendor'];
            }
            if (requestParameters['model'] != null) {
                queryParameters['model'] = requestParameters['model'];
            }
            if (requestParameters['mgmtAddress'] != null) {
                queryParameters['mgmtAddress'] = requestParameters['mgmtAddress'];
            }
            if (requestParameters['deviceType'] != null) {
                queryParameters['deviceType'] = requestParameters['deviceType'];
            }
            if (requestParameters['label'] != null) {
                queryParameters['label'] = requestParameters['label'];
            }
            if (requestParameters['q'] != null) {
                queryParameters['q'] = requestParameters['q'];
            }
            if (requestParameters['since'] != null) {
                queryParameters['since'] = requestParameters['since'].toISOString();
            }
            if (requestParameters['until'] != null) {
                queryParameters['until'] = requestParameters['until'].toISOString();
            }
            if (requestParameters['includeRunningConfigStatus'] != null) {
                queryParameters['includeRunningConfigStatus'] = requestParameters['includeRunningConfigStatus'];
            }
            if (requestParameters['sort'] != null) {
                queryParameters['sort'] = requestParameters['sort'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DevicesListResponseFromJSON(jsonValue));
        });
    }
    /**
     * Paginated list of devices in the organization. Default sort is `-lastSeenAt`. Filterable by site, state, device type, vendor, model, management address, and labels.
     * List devices
     */
    orgsOrgIdDevicesGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns clusters of device IDs that appear to represent the same physical device. Use this to present merge suggestions to users.
     * Get merge candidates
     */
    orgsOrgIdDevicesMergeGetRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesMergeGet().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/merge`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceMergeCandidatesFromJSON(jsonValue));
        });
    }
    /**
     * Returns clusters of device IDs that appear to represent the same physical device. Use this to present merge suggestions to users.
     * Get merge candidates
     */
    orgsOrgIdDevicesMergeGet(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesMergeGetRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Consolidates \'from\' devices into \'into\' device. On success, \'from\' devices will subsequently return 404 if requested.
     * Merge devices
     */
    orgsOrgIdDevicesMergePostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesMergePost().');
            }
            if (requestParameters['deviceMergeRequest'] == null) {
                throw new runtime.RequiredError('deviceMergeRequest', 'Required parameter "deviceMergeRequest" was null or undefined when calling orgsOrgIdDevicesMergePost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/merge`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: DeviceMergeRequestToJSON(requestParameters['deviceMergeRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceFromJSON(jsonValue));
        });
    }
    /**
     * Consolidates \'from\' devices into \'into\' device. On success, \'from\' devices will subsequently return 404 if requested.
     * Merge devices
     */
    orgsOrgIdDevicesMergePost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesMergePostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Creates a device record. Discovery-managed attributes are computed by the system when discovered.
     * Create device (testing/beta)
     */
    orgsOrgIdDevicesPostRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling orgsOrgIdDevicesPost().');
            }
            if (requestParameters['deviceCreate'] == null) {
                throw new runtime.RequiredError('deviceCreate', 'Required parameter "deviceCreate" was null or undefined when calling orgsOrgIdDevicesPost().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: DeviceCreateToJSON(requestParameters['deviceCreate']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => DeviceFromJSON(jsonValue));
        });
    }
    /**
     * Creates a device record. Discovery-managed attributes are computed by the system when discovered.
     * Create device (testing/beta)
     */
    orgsOrgIdDevicesPost(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.orgsOrgIdDevicesPostRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Deprecated ownership boundary. New clients must enqueue synchronization through the Integrations CMDB sync-request API. Inventory never executes customer-CMDB operations.
     * Sync CMDB data into REAP for inventory workflows
     * @deprecated
     */
    syncInventoryCMDBConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling syncInventoryCMDBConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling syncInventoryCMDBConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/sync`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCMDBSyncRequestToJSON(requestParameters['integrationConnectionCMDBSyncRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBSyncResponseFromJSON(jsonValue));
        });
    }
    /**
     * Deprecated ownership boundary. New clients must enqueue synchronization through the Integrations CMDB sync-request API. Inventory never executes customer-CMDB operations.
     * Sync CMDB data into REAP for inventory workflows
     * @deprecated
     */
    syncInventoryCMDBConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.syncInventoryCMDBConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Deprecated fail-closed boundary. Inventory-service rejects publication; clients must use the integrations-owned CMDB publication endpoint.
     * Legacy inventory CMDB publication endpoint
     * @deprecated
     */
    writebackInventoryCMDBConnectionRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling writebackInventoryCMDBConnection().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling writebackInventoryCMDBConnection().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/writeback`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: IntegrationConnectionCMDBWritebackRequestToJSON(requestParameters['integrationConnectionCMDBWritebackRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBWritebackResponseFromJSON(jsonValue));
        });
    }
    /**
     * Deprecated fail-closed boundary. Inventory-service rejects publication; clients must use the integrations-owned CMDB publication endpoint.
     * Legacy inventory CMDB publication endpoint
     * @deprecated
     */
    writebackInventoryCMDBConnection(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.writebackInventoryCMDBConnectionRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
}
/**
 * @export
 */
export const OrgsOrgIdDevicesGetDeviceTypeEnum = {
    All: 'all',
    Router: 'router',
    Switch: 'switch',
    Firewall: 'firewall',
    Other: 'other'
};
